import {
  DEFAULT_TRAILING_WINDOW_DAYS,
  TURN_TIME_DEFINITIONS_BASE_URL,
  TURN_TIME_METRICS_URL,
} from './turnTimeDefinitionApi.constants';

/**
 * Real client for the company-scoped turn-time definitions + compute endpoints
 * (DEV-3636, Skynet.Loans behind the admin/skynet gateway). The backend scopes
 * every query by the authenticated user's company, so no companyId is passed.
 *
 * Wire-shape adapter: the UI models a definition with a nested
 * `slaTarget: { value, unit }` and SCREAMING_SNAKE enums (e.g. `MILESTONE`,
 * `DAYS`), while the API uses flat `slaValue` / `slaUnit` and PascalCase enum
 * names (`Milestone`, `Days`). The two mappers below translate at the boundary
 * so the form / table / reducer keep the UI shape — without them create/edit
 * POST a nested slaTarget that never binds (SlaValue defaults to 0 → 400) and
 * reads of `slaTarget.value` / SCREAMING enums come back undefined.
 */

const toApiEnum = value => (value ? `${value.charAt(0)}${value.slice(1).toLowerCase()}` : value);

const toUiEnum = value => value?.toUpperCase() ?? value;

const toApiPayload = ({ slaTarget, startPoint, endPoint, ...rest }) => ({
  ...rest,
  startPoint: startPoint && { kind: toApiEnum(startPoint.kind), refId: startPoint.refId },
  endPoint: endPoint && { kind: toApiEnum(endPoint.kind), refId: endPoint.refId },
  slaValue: slaTarget?.value,
  slaUnit: toApiEnum(slaTarget?.unit),
});

const toUiDefinition = ({ slaValue, slaUnit, startPoint, endPoint, ...rest }) => ({
  ...rest,
  startPoint: startPoint && { kind: toUiEnum(startPoint.kind), refId: startPoint.refId },
  endPoint: endPoint && { kind: toUiEnum(endPoint.kind), refId: endPoint.refId },
  slaTarget: { value: slaValue, unit: toUiEnum(slaUnit) },
});

export class TurnTimeDefinitionApi {
  #http;

  constructor(http) {
    this.#http = http;
  }

  async getTurnTimeDefinitions(config = {}) {
    const { data: definitions } = await this.#http.get(TURN_TIME_DEFINITIONS_BASE_URL, {
      ...config,
      params: { includeInactive: true },
    });

    return { data: definitions.map(toUiDefinition) };
  }

  async createTurnTimeDefinition(body) {
    const { data } = await this.#http.post(TURN_TIME_DEFINITIONS_BASE_URL, toApiPayload(body));

    return { data: toUiDefinition(data) };
  }

  async updateTurnTimeDefinition({ id, ...body }) {
    const { data } = await this.#http.put(
      `${TURN_TIME_DEFINITIONS_BASE_URL}/${id}`,
      toApiPayload(body),
    );

    return { data: toUiDefinition(data) };
  }

  async deleteTurnTimeDefinition({ id }) {
    await this.#http.delete(`${TURN_TIME_DEFINITIONS_BASE_URL}/${id}`);

    return { data: { id } };
  }

  async getTurnTimeMetrics({
    trailingWindowDays = DEFAULT_TRAILING_WINDOW_DAYS,
    config = {},
  } = {}) {
    const { data } = await this.#http.get(TURN_TIME_METRICS_URL, {
      ...config,
      params: { trailingWindowDays },
    });

    return { data };
  }
}
